/**
 * May this user READ this chat?
 *
 * The send path has had an access check since it was written
 * (`validateChatAndAccess`), but the read path never did: `loadChat` takes a
 * chat id and nothing else, and the query layer runs on the SERVICE ROLE key,
 * so RLS is not a second line of defence. A chat id alone was enough to read
 * another employee's Meridian transcript — tool results, customer records and
 * whatever was said on a call included.
 *
 * This is the read-side counterpart, and it applies the SAME policy as the send
 * path, minus the two things a read must never do:
 *
 *   • it does not CREATE a missing chat (a read of nothing is a 404, not an
 *     invitation to mint a row), and
 *   • it does not REASSIGN a `slack-dm-` chat whose owner no longer matches
 *     (rewriting ownership as a side effect of a GET is not a read).
 *
 * What it does keep is the `slack-mention-` exception: a channel thread is
 * collaborative by nature, so any authenticated user may read it. That is a
 * deliberate carve-out, not an oversight — see `isSlackMentionChat`.
 */

import { getChatById } from '../db/queries';
import type { StoredChat } from '../db/queries';
import { chatAccessFor, isSlackMentionChat } from '../utils/chat-utils';

/**
 * `missing` and `deleted` are kept apart on purpose, and it is not a nicety.
 *
 * A chat row is created by the FIRST SEND, not by opening a tab, so a client
 * can legitimately ask for the history of an id that has no row yet — and the
 * honest answer to that is an empty conversation, exactly as it was before this
 * check existed. Collapsing it into a 404 would turn a normal race into an
 * error toast on the first message of every new chat.
 *
 * A row that exists and is soft-deleted is a different story: the caller is
 * asking for something that was really there and is gone.
 */
export type ChatReadAccess =
  | { ok: true; chat: StoredChat }
  | { ok: false; reason: 'missing' | 'deleted' | 'forbidden' };

/** Resolve a chat for reading, or say why not. */
export async function resolveChatForRead(
  chatId: string,
  userId: string,
): Promise<ChatReadAccess> {
  const chat = await getChatById(chatId);
  if (!chat) return { ok: false, reason: 'missing' };
  if (chat.status === 'deleted') return { ok: false, reason: 'deleted' };

  if (isSlackMentionChat(chatId)) return { ok: true, chat };
  if (chatAccessFor(userId, chat) === 'none') return { ok: false, reason: 'forbidden' };

  return { ok: true, chat };
}
